/**
 * INFINITY LIFE 2.0 — Main Application Orchestrator & Entry Point
 */

import { store } from './state.js';
import { api, showToast, syncOfflineQueue } from './api.js';
import { getCachedTasks, cacheTasks } from './db.js';
import { initPWA } from './pwa.js';
import { listenNotificationMessages } from './notifications.js';

// Layout & Components
import { renderHeader, attachHeaderEvents } from './components/Header.js';
import { renderNavbar, attachNavbarEvents } from './components/Navbar.js';
import { renderDashboard, attachDashboardEvents } from './components/Dashboard.js';
import { renderTaskList, attachTaskListEvents } from './components/TaskList.js';
import { renderTaskModal, attachTaskModalEvents } from './components/TaskModal.js';
import { renderNotificationCenter, attachNotificationCenterEvents } from './components/NotificationCenter.js';
import { renderAdminPanel, attachAdminPanelEvents } from './components/AdminPanel.js';
import { renderAuthModal, attachAuthModalEvents } from './components/AuthModal.js';
import { renderInstallPrompt, attachInstallPromptEvents } from './components/InstallPrompt.js';
import { renderBusinessView, attachBusinessViewEvents } from './components/BusinessView.js';
import { renderLandingPage, attachLandingPageEvents } from './components/LandingPage.js';
import { renderCheckoutModal, attachCheckoutModalEvents } from './components/CheckoutModal.js';
import { renderAiCopilot, attachAiCopilotEvents } from './components/AiCopilot.js';

let isRendering = false;

/**
 * Main application render function
 */
export function renderApp() {
  if (isRendering) return;
  isRendering = true;

  try {
    const app = document.getElementById('app');
    if (!app) return;

    const { currentView, user, activeMode } = store.get();

    // 1. Landing Page (Página de Vendas Inicial)
    if (currentView === 'landing') {
      app.innerHTML = `
        ${renderLandingPage()}
        <div id="modals-root">
          ${renderAuthModal()}
          ${renderCheckoutModal()}
          ${renderInstallPrompt()}
        </div>
      `;

      attachLandingPageEvents(app);
      attachAuthModalEvents(app);
      attachCheckoutModalEvents(app);
      attachInstallPromptEvents(app);
      return;
    }

    // 2. Painel da Aplicação Operacional (Módulos Liberados)
    let viewHtml = '';
    if (currentView === 'my-day') {
      viewHtml = renderDashboard();
    } else if (currentView === 'agenda') {
      viewHtml = renderTaskList();
    } else if (currentView === 'business') {
      viewHtml = renderBusinessView();
    } else if (currentView === 'admin') {
      const isOwner = !!user && (user.is_owner === true || user.role === 'owner' || user.email === 'admshuran@gmail.com');
      if (!isOwner) {
        // Usuários comuns ou visitantes não têm permissão para visualizar o painel admin
        store.setView(user ? 'my-day' : 'landing');
        return;
      }
      viewHtml = renderAdminPanel();
    } else {
      viewHtml = renderDashboard();
    }

    app.innerHTML = `
      <div class="app-container">
        ${renderNavbar()}
        <div class="main-wrapper">
          ${renderHeader()}
          <main class="content-body" id="main-content">
            ${viewHtml}
          </main>
        </div>
      </div>
      ${renderAiCopilot()}
      <div id="modals-root">
        ${renderTaskModal()}
        ${renderAuthModal()}
        ${renderNotificationCenter()}
        ${renderInstallPrompt()}
        ${renderCheckoutModal()}
      </div>
    `;

    // Attach reactive events
    attachNavbarEvents(app);
    attachHeaderEvents(app);

    if (currentView === 'my-day') {
      attachDashboardEvents(app);
    } else if (currentView === 'agenda') {
      attachTaskListEvents(app);
    } else if (currentView === 'business') {
      attachBusinessViewEvents(app);
    } else if (currentView === 'admin') {
      attachAdminPanelEvents(app);
    }

    attachAiCopilotEvents(app);
    attachTaskModalEvents(app);
    attachAuthModalEvents(app);
    attachNotificationCenterEvents(app);
    attachInstallPromptEvents(app);
    attachCheckoutModalEvents(app);
  } finally {
    isRendering = false;
  }
}



/**
 * Fetch and refresh all dashboard and tasks data
 */
export async function refreshAllData() {
  try {
    const [dashRes, tasksRes] = await Promise.allSettled([
      api.getMyDay(),
      api.getTasks(),
    ]);

    if (dashRes.status === 'fulfilled' && dashRes.value?.success) {
      store.set({ dashboardData: dashRes.value.data });
    }

    if (tasksRes.status === 'fulfilled' && tasksRes.value?.success) {
      const taskList = tasksRes.value.data || [];
      store.set({ tasks: taskList });
      await cacheTasks(taskList);
    }
  } catch (err) {
    console.warn('Erro ao atualizar dados:', err);
  }
}

/**
 * Refresh notifications
 */
export async function refreshNotifications() {
  try {
    const res = await api.getNotifications();
    if (res.success) {
      const notifs = res.data?.notifications || [];
      const unread = res.data?.unread_count ?? notifs.filter((n) => !n.is_read).length;
      store.set({
        notifications: notifs,
        unreadNotificationsCount: unread,
      });
    }
  } catch (err) {
    // ignore when offline or unauthenticated
  }
}

/**
 * Bootstrap the application on page load
 */
async function initApp() {
  // 1. Set initial theme
  const savedTheme = localStorage.getItem('infinity_theme') || 'dark';
  document.documentElement.setAttribute('data-theme', savedTheme);

  // 2. Load cached tasks immediately from IndexedDB for zero-lag startup
  try {
    const cached = await getCachedTasks();
    if (cached && cached.length > 0) {
      store.set({ tasks: cached });
    }
  } catch (e) {
    console.warn('Erro ao carregar cache local:', e);
  }

  // 3. Subscribe renderApp to reactive store
  store.subscribe(() => {
    renderApp();
  });

  // Initial render
  renderApp();

  // 4. Initialize PWA & Notifications
  initPWA();
  listenNotificationMessages();

  // 5. Check user session if token exists
  const token = localStorage.getItem('infinity_token');
  if (token) {
    try {
      const meRes = await api.getMe();
      if (meRes.success && meRes.user) {
        store.updateUser(meRes.user);
        if (meRes.unread_notifications !== undefined) {
          store.set({ unreadNotificationsCount: meRes.unread_notifications });
        }
      } else {
        localStorage.removeItem('infinity_token');
      }
    } catch (e) {
      console.warn('Sessão expirada ou indisponível:', e);
    }
  }

  // 6. Fetch live data
  await refreshAllData();
  await refreshNotifications();

  // 7. Sincronizar operações offline se estiver online
  if (navigator.onLine) {
    syncOfflineQueue();
  }

  // 8. Event listeners globais
  window.addEventListener('online', () => {
    store.set({ isOnline: true });
    showToast('Conexão restabelecida. Sincronizando dados...', 'success');
    syncOfflineQueue();
    refreshAllData();
  });

  window.addEventListener('offline', () => {
    store.set({ isOnline: false });
    showToast('Você está offline. Alterações serão salvas localmente.', 'info');
  });

  window.addEventListener('infinity:refresh-data', refreshAllData);
  window.addEventListener('infinity:refresh-notifications', refreshNotifications);
  window.addEventListener('infinity:render', () => renderApp());
  window.addEventListener('infinity:rerender-view', () => renderApp());

  // Atalhos de teclado ergonômicos
  window.addEventListener('keydown', (e) => {
    // Atalho "/" para busca global
    if (e.key === '/' && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
      e.preventDefault();
      const search = document.getElementById('global-search');
      if (search) search.focus();
    }

    // Atalho "Escape" para fechar modais
    if (e.key === 'Escape') {
      const state = store.get();
      if (state.modals.task.isOpen) store.closeTaskModal();
      if (state.modals.auth) store.toggleModal('auth', false);
      if (state.modals.notifications) store.toggleModal('notifications', false);
      if (state.modals.installPrompt) store.toggleModal('installPrompt', false);
    }
  });
}

// Start application when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}
